/** XeAgents.ai - Voice LLM Settings for ElevenLabs Agents **/
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Save, Loader2, Volume2, DollarSign } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";

// ElevenLabs supported models with pricing info
const VOICE_LLM_MODELS = [
  {
    value: "gpt-4.1",
    label: "GPT-4.1",
    description: "Recommended - follows scripted flows most reliably",
    cost: "$$$$$"
  },
  {
    value: "gpt-4.1-mini",
    label: "GPT-4.1 Mini",
    description: "Faster replies than GPT-4.1 - check it still follows scripted flows",
    cost: "$$"
  },
  {
    value: "gpt-4o-mini",
    label: "GPT-4o Mini",
    description: "Fast & cheap - Best for cost savings",
    cost: "$$$"
  },
  {
    value: "gpt-4o",
    label: "GPT-4o",
    description: "High quality, balanced speed",
    cost: "$$$$$"
  },
  {
    value: "claude-sonnet-4-6",
    label: "Claude Sonnet 4.6",
    description: "Excellent reasoning, premium quality",
    cost: "$$$$$$"
  },
  {
    value: "claude-sonnet-4-5",
    label: "Claude Sonnet 4.5",
    description: "Great quality, good reasoning",
    cost: "$$$$$"
  },
  {
    value: "gemini-2.0-flash",
    label: "Gemini 2.0 Flash",
    description: "Fast Google model",
    cost: "$$$$"
  },
  {
    value: "gemini-2.5-flash",
    label: "Gemini 2.5 Flash",
    description: "Latest Gemini, fast",
    cost: "$$$$"
  },
];

export default function VoiceLLMSettings() {
  const { toast } = useToast();
  const [selectedModel, setSelectedModel] = useState("gpt-4o-mini");

  // Fetch settings
  const { data: settingsData } = useQuery<any>({ queryKey: ["/api/admin/settings"] });
  const settings = settingsData?.data || settingsData;

  // Initialize from settings
  useEffect(() => {
    if (settings?.voice_llm_model) {
      setSelectedModel(settings.voice_llm_model);
    }
  }, [settings]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      return apiRequest("PATCH", `/api/admin/settings/${key}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
  });

  const handleSave = async () => {
    try {
      await updateSetting.mutateAsync({
        key: "voice_llm_model",
        value: selectedModel,
      });

      toast({
        title: "Voice LLM Settings Saved",
        description: `ElevenLabs agents will now use ${selectedModel}`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to Save",
        description: error.message || "Could not save voice LLM settings",
        variant: "destructive",
      });
    }
  };

  const selectedModelInfo = VOICE_LLM_MODELS.find(m => m.value === selectedModel);

  return (
    <Card id="voice-llm-settings" className="border-2 border-orange-500/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5 text-orange-500" />
          Voice Agent LLM (ElevenLabs)
        </CardTitle>
        <CardDescription>
          Configure the LLM model used by ElevenLabs voice agents. This is separate from the Conversational LLM setting above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900">
          <DollarSign className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <AlertDescription className="text-sm text-orange-800 dark:text-orange-200">
            <strong>Cost Tip:</strong> GPT-4o Mini is the most cost-effective option for voice agents while maintaining good quality. Claude models cost more but provide better reasoning.
          </AlertDescription>
        </Alert>

        {/* Voice LLM Model Selection */}
        <div className="space-y-2">
          <div className="flex items-center">
            <Label>Voice LLM Model</Label>
            <InfoTooltip content="This model is used by all ElevenLabs voice agents for conversation" />
          </div>
          <Select
            value={selectedModel}
            onValueChange={setSelectedModel}
          >
            <SelectTrigger data-testid="select-voice-llm-model">
              <SelectValue placeholder="Select voice LLM model..." />
            </SelectTrigger>
            <SelectContent>
              {VOICE_LLM_MODELS.map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{model.label}</span>
                      <span className="text-xs text-green-600 dark:text-green-400">{model.cost}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {model.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedModelInfo && (
            <p className="text-sm text-muted-foreground">
              Selected: <strong>{selectedModelInfo.label}</strong> - {selectedModelInfo.description}
            </p>
          )}
        </div>

        {/* Current setting info */}
        {settings?.voice_llm_model && (
          <div className="text-sm p-3 bg-muted rounded-md">
            <strong>Current Setting:</strong> {settings.voice_llm_model}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={updateSetting.isPending}
            data-testid="button-save-voice-llm"
          >
            {updateSetting.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Voice LLM Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
